// frontend/src/pages/FeesPage.js
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { H1, Box, Button, InlineMessage, Modal } from '@bigcommerce/big-design';
import { MoreHorizIcon } from '@bigcommerce/big-design-icons';
import { getFees, deleteFee } from '../api/fees';
import { Dropdown } from '@bigcommerce/big-design';
import { GlobalStyles } from '@bigcommerce/big-design';
 

const formatValue = (value) => {
  const num = Number(value);
  return Number.isInteger(num) ? num.toString() : value.toString();
};


// --- Fee Actions Dropdown (Edit/Delete) ---
const FeeActions = ({ feeId, feeName, fee, onFeeDeleted, setSuccessMessage }) => {
  const navigate = useNavigate();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleEdit = () => {
    navigate(`/fees/edit/${fee.id}`);
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await deleteFee(feeId);
      onFeeDeleted(feeId);
      setSuccessMessage("Fee deleted successfully!");
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setSuccessMessage(""), 3000);
      setIsConfirmOpen(false);
    } catch (error) {
      console.error("Failed to delete fee", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <Dropdown
        items={[
          { content: "Edit", onItemClick: handleEdit },
          { content: "Delete", onItemClick: () => setIsConfirmOpen(true), actionType: "destructive" },
        ]}
        toggle={<Button variant="subtle" iconOnly={<MoreHorizIcon color="secondary60" />} ariaLabel="Actions" />}
        placement="bottom-end"
      />
      <Modal
        isOpen={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        header="Delete Fee"
        actions={[
          { text: "Cancel", onClick: () => setIsConfirmOpen(false) },
          { text: isDeleting ? "Deleting..." : "Delete", onClick: handleDelete, variant: "secondary", isLoading: isDeleting },
        ]}
      >
        Are you sure you want to delete <strong>{feeName}</strong>?
      </Modal>
    </>
  );
};

function FeesPage() {
  const navigate = useNavigate();
  const [fees, setFees] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => { 
    fetchFees(); 
    
    // Check for success message from localStorage (set by CreateEditFeePage)
    const savedMessage = localStorage.getItem('feeSuccessMessage');
    if (savedMessage) {
      setSuccessMessage(savedMessage);
      localStorage.removeItem('feeSuccessMessage');
      setTimeout(() => setSuccessMessage(""), 3000);
    }
  }, []);

  async function fetchFees() {
    try {
      const data = await getFees();
      setFees(data);
    } catch (err) {
      console.error("Failed to fetch fees", err);
      setErrorMessage("Failed to load fees.");
    }
  }

  const handleCreateFee = () => {
    navigate('/fees/create');
  };


  return (
    <>
      <GlobalStyles />
      <Box margin="xxLarge" padding="large">
        <Box marginBottom="medium">
          <H1>Fees</H1>
          <p style={{ marginBottom: "1.5rem", color: "#666" }}>Manage your fees</p>
          <Button variant="primary" marginBottom="large" onClick={handleCreateFee}>
            Create Fee
          </Button>
        </Box>
        
            {successMessage && <InlineMessage type="success" messages={[{ text: successMessage }]} />}
            {errorMessage && <InlineMessage type="error" messages={[{ text: errorMessage }]} />}
        

        <Box overflowX="auto">
          {fees.length === 0 ? (
            <Box
              padding="xxLarge"
              style={{
                textAlign: "center",
                backgroundColor: "#f5f7fa",
                borderRadius: "8px",
                border: "1px dashed #d1d5db"
              }}
            >
              <p style={{ fontSize: "1.1rem", color: "#666", marginBottom: "0.5rem" }}>
                No fees have been created yet.
              </p>
              <p style={{ color: "#888", fontSize: "0.9rem" }}>
                Click "Create Fee" to add your first checkout fee.
              </p>
            </Box>
          ) : (
            <table className="fees-table" style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr><th>Name</th><th>Display Name</th><th>Type</th><th>Amount</th><th>Enabled</th><th></th></tr>
              </thead>
              <tbody>{fees.map(fee => (
                <tr key={fee.id} style={{ borderTop: "1px solid #ddd" }}>
                  <td>{fee.name}</td><td>{fee.display_name || fee.name}</td>
                  <td>{fee.fee_type === "amount" ? "Amount ($)" : "Percentage (%)"}</td>
                  <td>{fee.fee_type === "amount" ? `$${formatValue(fee.amount)}` : `${formatValue(fee.amount)}%`}</td>
                  <td>{fee.enabled ? "Yes" : "No"}</td>
                  <td><FeeActions feeId={fee.id} feeName={fee.name} fee={fee}
                    onFeeDeleted={id => setFees(prev => prev.filter(f => f.id !== id))}
                    setSuccessMessage={setSuccessMessage} /></td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </Box>

      </Box>
    </>
  );
}

  


export default FeesPage;
